import React, { Fragment } from 'react';
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  InputGroup, InputGroupText,
  Table,
  FormFeedback,
  Alert,
  Form,
} from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import {
  SET_ADDITIONAL_DOCUMENT_URL,
  APPLICATION_STEP_ONE_SET_FIELD_VALUES,
  GOTO_NEXT_STEP,
} from '../../../redux/actions/ActionTypes';
import { FILE_UPLOAD_URL } from '../../../utils/APIEndpoints';
import { authfileHeader } from '../../../utils/functions/CoreFunctions';
import API from '../../../redux/actions/API';
import Swal from 'sweetalert2';
import {
  getAlertToast,
  SingleStringFirstLetterCaps,
} from '../../../utils/functions/CustomFunctions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TEN_DIGIT_PHONE_NUMBER_REGEX,
  NUMBER_REGEX,
} from '../../../utils/constants/ValidationRegexs';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  phoneNumber: '',
  postalcode: '',
  city: '',
  state: '',
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  email: Yup.string()
    .trim()
    .required('Email is required')
    .email('Enter valid email'),
  address: Yup.string().trim().required('Address is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .matches(TEN_DIGIT_PHONE_NUMBER_REGEX, 'Enter valid Phone number'),
  postalcode: Yup.string()
    .required('Postal code is required')
    .matches(NUMBER_REGEX, 'Enter Valid Postal code'),
  city: Yup.string().trim().required('City is required'),
  state: Yup.string().trim().required('State is required'),
});

function STEP_ONE_BASIC_DETAILS(props) {
  const { documentDetails = {} } = props;
  //hooks
  const ApplicationFormState = useSelector((state) => state.ApplicationForm);
  const { step1, currentStep } = ApplicationFormState;
  const { additionalDocuments } = step1;
  const dispatch = useDispatch();
  let form = useSelector((state) => state.InstructionReducer);
  const { formValues = [] } = form;

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched
  } = useFormik({
    initialValues: initialForm,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      console.log('Values', values, formValues);
      let result = checkDocumentsUploaded();

      if (result) {
        dispatch({
          type: APPLICATION_STEP_ONE_SET_FIELD_VALUES,
          payload: values,
        });
        dispatch({
          type: GOTO_NEXT_STEP,
          payload:
            currentStep == 1 && (formValues == null || formValues.length == 0)
              ? currentStep + 1
              : currentStep,
        });
      }
    },
  });

  // Document Upload
  function checkDocumentsUploaded() {
    if (additionalDocuments && additionalDocuments.length) {
      let unFilledDocs = additionalDocuments.find(
        (document) => document.isRequired && !document.documentURL
      );
      if (unFilledDocs) {
        Swal.fire(getAlertToast('error', 'Please upload required documents'));
        return false;
      }
    }
    return true;
  }

  const handleUpload = async (e) => {
    e.preventDefault();
    const documentId = e.currentTarget.dataset.document_id;
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    const options = {
      method: 'POST',
      url: FILE_UPLOAD_URL,
      headers: authfileHeader(),
      data: formData,
    };
    try {
      Swal.fire(getAlertToast('success', 'Please wait...'));
      var apiResponse = await API(options);
      const { status, filepath } = apiResponse.data;
      if (status === 'Success') {
        Swal.fire(getAlertToast('success', 'File uploaded successfully.'));
        dispatch({
          type: SET_ADDITIONAL_DOCUMENT_URL,
          payload: filepath,
          documentId,
        });
      }
    } catch (error) {
      if (error.response) {
        Swal.fire(
          getAlertToast('error', 'File upload failed, please try again!')
        );
      }
    }
  };

  const {
    serviceDescription = '',
    serviceName = '',
    serviceAmount = 0,
  } = documentDetails;


  return (
    <Fragment>
      <Row>
        <Col md={1} />
        <Col md={10}>
          <Alert color="info">
            <h4 className="alert-heading">{serviceName}</h4>
            {serviceAmount && parseInt(serviceAmount) > 0 ? (
              <span
                style={{
                  float: 'right',
                  marginTop: '-32px',
                  fontSize: '24px',
                  fontWeight: '700',
                }}
              >
                ${parseFloat(serviceAmount).toFixed(2)}
              </span>
            ) : (
              ''
            )}
            <hr />
            {/* <p>{SingleStringFirstLetterCaps(serviceDescription)}</p> */}
            <div
              className="view ql-editor"
              dangerouslySetInnerHTML={{
                __html: serviceDescription || '',
              }}
            ></div>
          </Alert>
        </Col>
        <Col md={1} />
      </Row>
      <Form onSubmit={handleSubmit} id="basic-details">
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="firstName">First Name *</Label>
              <Input
                type="text"
                name="firstName"
                id="firstName"
                placeholder="Enter First Name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={SingleStringFirstLetterCaps(values.firstName)}
                invalid={errors.firstName && touched.firstName ? true : false}
              />
              <FormFeedback>
                {errors.firstName && touched.firstName ? errors.firstName : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="lastName">Last Name *</Label>
              <Input
                type="text"
                name="lastName"
                id="lastName"
                placeholder="Enter Last Name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={SingleStringFirstLetterCaps(values.lastName)}
                invalid={errors.lastName && touched.lastName ? true : false}
              />
              <FormFeedback>
                {errors.lastName && touched.lastName ? errors.lastName : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="email">Email *</Label>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder="Enter Email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                invalid={errors.email && touched.email ? true : false}
              />
              <FormFeedback>
                {errors.email && touched.email ? errors.email : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="email">Phone Number *</Label>
              <InputGroup>
                
                  <InputGroupText>+1</InputGroupText>
                
                <Input
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  placeholder="Phone Number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.phoneNumber}
                  invalid={
                    errors.phoneNumber && touched.phoneNumber ? true : false
                  }
                />
                <FormFeedback>
                  {errors.phoneNumber && touched.phoneNumber
                    ? errors.phoneNumber
                    : ''}
                </FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="address">Address *</Label>
              <Input
                type="textarea"
                name="address"
                id="address"
                placeholder="Enter Address"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.address}
                invalid={errors.address && touched.address ? true : false}
              />
              <FormFeedback>
                {errors.address && touched.address ? errors.address : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="postalcode">Postal Code *</Label>
              <Input
                type="text"
                name="postalcode"
                id="postalcode"
                placeholder="Enter Postal Code"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.postalcode}
                invalid={errors.postalcode && touched.postalcode ? true : false}
              />
              <FormFeedback>
                {errors.postalcode && touched.postalcode
                  ? errors.postalcode
                  : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="city">City *</Label>
              <Input
                type="text"
                name="city"
                id="city"
                placeholder="Enter City"
                onChange={handleChange}
                onBlur={handleBlur}
                value={SingleStringFirstLetterCaps(values.city)}
                invalid={errors.city && touched.city ? true : false}
              />
              <FormFeedback>
                {errors.city && touched.city ? errors.city : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="state">State *</Label>
              <Input
                type="text"
                name="state"
                id="state"
                placeholder="Enter State"
                onChange={handleChange}
                onBlur={handleBlur}
                value={SingleStringFirstLetterCaps(values.state)}
                invalid={errors.state && touched.state ? true : false}
              />
              <FormFeedback>
                {errors.state && touched.state ? errors.state : ''}
              </FormFeedback>
            </FormGroup>
          </Col>
          {/* <hr /> */}
          {step1.additionalDocuments.length > 0 && (
            <React.Fragment>
              <Col md={10}>
                <h6>Additional Documents</h6>
                <Alert color="secondary">
                  <i
                    title="Required Document"
                    className="fas fa-exclamation-circle"
                    style={{ color: '#c72e39' }}
                  />{' '}
                  - Required Documents
                </Alert>
                <Table responsive bordered>
                  <thead>
                    <tr>
                      <th>Document Name</th>
                      <th>Description</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {step1.additionalDocuments.map((item, index) => (
                      <tr key={`add_list_${index}`}>
                        <td style={{ width: '250px' }}>
                          {item.documentName || '-'}
                        </td>
                        <td>{item.description || '-'}</td>
                        <td style={{ width: '250px' }}>
                          <label
                            style={{ marginBottom: '0' }}
                            className="btn-bs-file btn btn-lg btn-info"
                          >
                            <i className="fas fa-file-upload"></i> Upload File
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              name="file"
                              data-document_id={item.documentId}
                              onChange={handleUpload}
                            />
                          </label>
                          {!!item.isRequired && (
                            <i
                              title="Required Document"
                              className="fas fa-exclamation-circle"
                              style={{ color: '#c72e39', marginLeft: '8px' }}
                            ></i>
                          )}
                          {item.documentURL && (
                            <Fragment>
                              <i
                                className="far fa-check-circle"
                                style={{
                                  marginLeft: '9px',
                                  fontSize: '17px',
                                  color: '#18bb0e',
                                }}
                              ></i>
                              <a
                                title="View File"
                                href={item.documentURL}
                                target="_blank"
                              >
                                <i
                                  className="far fa-file"
                                  style={{
                                    marginLeft: '9px',
                                    fontSize: '17px',
                                    color: '#2689ef',
                                  }}
                                ></i>
                              </a>
                            </Fragment>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
              <Col md={1} />
            </React.Fragment>
          )}
        </Row>
      </Form>
    </Fragment>
  );
}
export default STEP_ONE_BASIC_DETAILS;

